/**
 * URL-persisted analytics context: snapshot, baseline, and global filters.
 * Used by AnalyticsContextBar and analytics pages for shareable views.
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface AnalyticsFilters {
  wbs: string[];
  activityCode: string[];
  status: string[];
}

const FILTER_KEYS = ['wbs', 'activityCode', 'status'] as const;
const PARAM_SNAPSHOT = 'snapshot';
const PARAM_BASELINE = 'baseline';

function parseMultiParam(value: string | null): string[] {
  if (!value || value.trim() === '') return [];
  return value.split(',').map((s) => s.trim()).filter(Boolean);
}

function serializeMultiParam(values: string[]): string | null {
  const trimmed = values.filter(Boolean);
  return trimmed.length === 0 ? null : trimmed.join(',');
}

export interface UseAnalyticsContextResult {
  snapshotId: string | null;
  baselineId: string | null;
  filters: AnalyticsFilters;
  setSnapshotId: (id: string | null) => void;
  setBaselineId: (id: string | null) => void;
  setFilter: (key: keyof AnalyticsFilters, values: string[]) => void;
  resetAll: () => void;
}

export function useAnalyticsContext(): UseAnalyticsContextResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const snapshotId = searchParams.get(PARAM_SNAPSHOT) ?? null;
  const baselineId = searchParams.get(PARAM_BASELINE) ?? null;

  const filters: AnalyticsFilters = useMemo(
    () => ({
      wbs: parseMultiParam(searchParams.get('wbs')),
      activityCode: parseMultiParam(searchParams.get('activityCode')),
      status: parseMultiParam(searchParams.get('status')),
    }),
    [searchParams]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === '') next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSnapshotId = useCallback(
    (id: string | null) => {
      updateParams({ [PARAM_SNAPSHOT]: id });
    },
    [updateParams]
  );

  const setBaselineId = useCallback(
    (id: string | null) => {
      updateParams({ [PARAM_BASELINE]: id });
    },
    [updateParams]
  );

  const setFilter = useCallback(
    (key: keyof AnalyticsFilters, values: string[]) => {
      const paramValue = serializeMultiParam(values);
      updateParams({ [key]: paramValue });
    },
    [updateParams]
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PARAM_SNAPSHOT);
        next.delete(PARAM_BASELINE);
        FILTER_KEYS.forEach((k) => next.delete(k));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return {
    snapshotId,
    baselineId,
    filters,
    setSnapshotId,
    setBaselineId,
    setFilter,
    resetAll,
  };
}
